/**
 * The online lobby (GDD section 2): team name, create a room (players, invaders per player, rounds before the end of
 * the world; public or private) or join one with its code, its link or from the list of the public rooms, then everyone
 * declares themselves ready and the host launches.
 * In a Discord Activity (author's mode) there is nothing to create: the players of the Activity join its room, whose
 * settings are fixed (the server's), and their team name is their Discord name.
 * When the match starts, the lobby hands the connection over to the match page (onStart).
 */
import { MAP_H, MAP_W, decodeArsenal, encodeArsenal, maxUnitsPerPlayer, type ScenarioDesc } from '@invaders/game';
import { arsenalSummary, isDefaultArsenal, setupArsenalPicker } from '../shared/arsenal-ui';
import { fillMapSelect, mapLabel, resolveMap } from '../shared/maps-ui';
import { loadOptions } from '../shared/options';
import { copyText, selectText } from '../shared/clipboard';
import { NetClient } from '../net/net-client';
import { SERVER_URL } from '../net/config';
import type { FoundMessage, ListedRoom, PingRule, RoomMessage, RoomSettings, StartMessage } from '../net/protocol';
import { MAX_TEAM_NAME, TEAM_COLORS } from '../shared/teams';
import { discordCode, discordInstance } from '../shared/discord';
import { track } from '../shared/analytics';

export interface LobbyHooks {
  /** The match starts (or starts again for a browser that comes back): the connection is the match's now. */
  onStart(msg: StartMessage, net: NetClient, room: RoomMessage): void;
  /** Every update of the room, also during the match (players connected or not). */
  onRoom(room: RoomMessage): void;
  /** The host excluded this browser: the match (if any) is left, the lobby shows why. */
  onKicked(message: string): void;
}

/** A second click within this delay confirms an exclusion. */
export const KICK_CONFIRM_MS = 3000;

/** Remembered per tab, to come back to the same seat after a reload. */
const SESSION_KEY = 'invaders.session';
/** The code of the last room this browser created: asked for again at the next one (the friends' link stays good). */
const CODE_KEY = 'invaders.roomCode';

/** The host's last choice of a public or private room. */
const PUBLIC_KEY = 'invaders.roomPublic';
/** The list of the public rooms is asked for again this often while the « Join » tab is shown. */
const LIST_EVERY_MS = 3000;
/** Discord: the room of the Activity is looked for this often while the « Create a room » tab is shown. */
const FIND_EVERY_MS = 2000;

function lastCode(): string | undefined {
  try { return localStorage.getItem(CODE_KEY) ?? undefined; } catch { return undefined; }
}

function keepCode(code: string): void {
  try { localStorage.setItem(CODE_KEY, code); } catch { /* private window: a new code every time */ }
}

type HostOptions = Pick<RoomSettings, 'turn' | 'friendlyFire' | 'hidden' | 'free' | 'ghosts'>;

/** The host's options as MatchConfig fields (left out when they are the GDD's). */
export function matchOptions(s: HostOptions): { turnSeconds?: number; friendlyFire?: boolean; hiddenPlacement?: boolean; autoPlacement?: boolean; ghostTurns?: boolean } {
  return {
    ...(s.turn !== undefined && s.turn !== 30 ? { turnSeconds: s.turn } : {}),
    ...(s.friendlyFire === false ? { friendlyFire: false } : {}),
    ...(s.hidden === true && s.free !== false ? { hiddenPlacement: true } : {}),
    ...(s.free === false ? { autoPlacement: true } : {}),
    ...(s.ghosts === true ? { ghostTurns: true } : {}),
  };
}

/** The host's options in a few words, for the room. */
export function optionsSummary(s: HostOptions & Pick<RoomSettings, 'pings'>): string {
  const placement = s.free === false ? 'placement by the computer' : s.hidden ? 'hidden placement' : 'free placement';
  return [
    `${s.turn ?? 30} s turns`, s.friendlyFire === false ? 'no friendly fire' : 'friendly fire', placement,
    ...(s.ghosts ? ['ghost turns'] : []), ...(s.pings === 'none' ? ['no pings'] : s.pings === 'all' ? ['spectator pings'] : []),
  ].join(' · ');
}

/** Who may ping (author's option), in the order of the slider of the lobby, and their names. */
const PING_RULES: readonly PingRule[] = ['none', 'players', 'all'];
const PING_NAMES = ['Nobody', 'Players only', 'Players + spectators'];

/**
 * Invaders per player at the start: the host's choice, or in a Discord Activity's room the most for the players there
 * (`seated`, 2 at least: a match needs them).
 */
export function unitsAtStart(s: RoomSettings, seated: number): number {
  return s.discord ? maxUnitsPerPlayer(Math.max(2, seated)) : s.units;
}

/**
 * Every rule of a room in a few words: for the room itself and for the list of the public rooms. `seated`: the players in
 * the room now (a Discord Activity's room gives them the most invaders they may have).
 */
export function rulesSummary(s: RoomSettings, seated = s.players): string {
  const weapons = decodeArsenal(s.arsenal ?? '');
  const armory = weapons.ammo && weapons.chestWeapons ? arsenalSummary({ ammo: weapons.ammo, chestWeapons: weapons.chestWeapons }) : 'Standard arsenal';
  const units = unitsAtStart(s, seated);
  const most = s.discord ? ` (the most for ${Math.max(2, seated)} players)` : '';
  return `${mapLabel(s.map)} · ${units} invader${units > 1 ? 's' : ''} per player${most} · ${s.lava} rounds before the end of the world · ${optionsSummary(s)} · ${armory}`;
}

/**
 * « Placement libre » unchecked: the computer places the invaders, so « Placement caché » means nothing and is greyed
 * out (unchecked). Shared by the lobby and the local setup.
 */
export function linkPlacementOptions(free: HTMLInputElement, hidden: HTMLInputElement): void {
  const sync = () => {
    hidden.disabled = !free.checked;
    if (!free.checked) hidden.checked = false;
    hidden.closest('label')?.classList.toggle('disabled', !free.checked);
  };
  free.addEventListener('change', sync);
  sync();
}

/** `watch`: a spectator (no token), who watches the room again after a reload. */
interface Saved { code: string; token: string; name: string; watch?: boolean }

function loadSaved(): Saved | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch { return null; }
}

function save(s: Saved | null): void {
  try {
    if (s) sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch { /* private window: no reload recovery */ }
}

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

function escapeHtml(t: string): string {
  return t.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

export function roomLink(code: string): string {
  return `${location.origin}${location.pathname}?room=${code}`;
}

/** A row of buttons, one per number (players, invaders per player): the one picked is highlighted. */
function segmented(id: string, initial: number, onPick: (v: number) => void): { pick(v: number): void; buttons: HTMLButtonElement[] } {
  const buttons = [...$(id).querySelectorAll('button')];
  const pick = (v: number) => {
    buttons.forEach((b) => b.classList.toggle('on', Number(b.textContent) === v));
    onPick(v);
  };
  buttons.forEach((b) => { b.onclick = () => pick(Number(b.textContent)); });
  pick(initial);
  return { pick, buttons };
}

/**
 * The size of the match, with the same controls online and on this PC (`prefix`: 'o-' and ''): players (2 to 8) and
 * invaders per player (1 to 4) as rows of buttons, the rounds before the end of the world on a slider. 16 invaders at
 * most on the map: the counts of invaders that would go over are greyed out (and the choice comes down). `onPlayers`:
 * the number of players changed (called once at the start too).
 */
export function setupMatchSize(prefix: string, onPlayers?: (n: number) => void): { players(): number; units(): number; lava(): number } {
  const chosen = { players: 2, units: 3 };
  const units = segmented(`${prefix}units`, chosen.units, (v) => { chosen.units = v; });
  segmented(`${prefix}players`, chosen.players, (v) => {
    chosen.players = v;
    const most = maxUnitsPerPlayer(v);
    units.buttons.forEach((b) => { b.disabled = Number(b.textContent) > most; });
    if (chosen.units > most) units.pick(most);
    onPlayers?.(v);
  });
  const lava = $<HTMLInputElement>(`${prefix}lava`);
  lava.oninput = () => { $(`${prefix}lava-out`).textContent = lava.value; };
  return { players: () => chosen.players, units: () => chosen.units, lava: () => Number(lava.value) };
}

export function setupLobby(hooks: LobbyHooks): { leave(): void; kick(seat: number): void } {
  let net: NetClient | null = null;
  let room: RoomMessage | null = null;
  let ready = false;
  let creating = false;
  /** The host's first click on « exclure »: which seat, until when a second one confirms it. */
  let kickArmed = { seat: -1, until: 0 };

  const error = (text: string) => { $('o-error').textContent = text; };
  const arsenal = setupArsenalPicker($('o-arsenal-btn'), $('o-arsenal-sum'));

  // --- form ---------------------------------------------------------------------------------------
  const size = setupMatchSize('o-');
  linkPlacementOptions($<HTMLInputElement>('o-free'), $<HTMLInputElement>('o-hidden'));
  // Who may ping: a slider between the three choices (the players only by default).
  const pings = $<HTMLInputElement>('o-pings');
  pings.oninput = () => { $('o-pings-out').textContent = PING_NAMES[Number(pings.value)]; };
  // The host draws the seed with the choice of the map, so that the preview is the map that will be played.
  let previewSeed = (Math.random() * 2 ** 32) >>> 0;
  // Public (listed on everyone's « Join » tab) or private (code or link only); the last choice is kept.
  let listed = true;
  try { listed = localStorage.getItem(PUBLIC_KEY) !== '0'; } catch { /* private window: public */ }
  const visibility = [...$('o-visibility').querySelectorAll('button')];
  const pickVisibility = (v: boolean) => {
    listed = v;
    visibility.forEach((b) => b.classList.toggle('on', (b.dataset.v === 'public') === v));
  };
  visibility.forEach((b) => {
    b.onclick = () => {
      pickVisibility(b.dataset.v === 'public');
      try { localStorage.setItem(PUBLIC_KEY, listed ? '1' : '0'); } catch { /* not kept */ }
    };
  });
  pickVisibility(listed);
  // Discord: no room to create nor to look for, the Activity's to join; the link of the website means nothing in
  // Discord's frame (the code is still shown).
  if (discordInstance) {
    for (const id of ['online-tabs', 'sub-create', 'sub-join']) $(id).classList.add('hidden');
    $('d-lobby').classList.remove('hidden');
    document.querySelector('.room-link')?.classList.add('hidden');
  }
  const map = $<HTMLSelectElement>('o-map');
  fillMapSelect(map, $('o-map-blurb'), $<HTMLCanvasElement>('o-map-preview'), () => previewSeed);
  map.addEventListener('change', () => { previewSeed = (Math.random() * 2 ** 32) >>> 0; }, { capture: true });

  const subTabs = [...$('online-tabs').querySelectorAll('button')];
  const showSub = (sub: 'create' | 'join') => {
    subTabs.forEach((b) => b.classList.toggle('on', b.dataset.sub === sub));
    $('sub-create').classList.toggle('hidden', sub !== 'create');
    $('sub-join').classList.toggle('hidden', sub !== 'join');
    if (sub === 'join') void refreshList();
  };
  subTabs.forEach((b) => { b.onclick = () => showSub(b.dataset.sub as 'create' | 'join'); });

  const name = (): string | null => {
    const n = $<HTMLInputElement>('o-name').value.trim();
    if (n.length < 1 || n.length > MAX_TEAM_NAME) { error(`A team name is required, ${MAX_TEAM_NAME} characters at most.`); return null; }
    return n;
  };

  // --- connection -------------------------------------------------------------------------------
  let connecting: Promise<NetClient | null> | null = null;
  /** `quiet`: for the list of the public rooms, which says it itself (no error under the form every 3 s). */
  function connect(quiet = false): Promise<NetClient | null> {
    if (net?.open) return Promise.resolve(net);
    connecting ??= openConnection(quiet).finally(() => { connecting = null; });
    return connecting;
  }

  async function openConnection(quiet: boolean): Promise<NetClient | null> {
    const client = new NetClient(SERVER_URL);
    try {
      await client.connect();
    } catch {
      // The public site has nothing to start: only in development does the server have to be launched by hand.
      const why = import.meta.env.DEV ? `Server unreachable (${SERVER_URL}): run "pnpm server".` : 'Server unreachable: try again in a moment.';
      if (quiet) $('o-rooms-note').textContent = why;
      else error(why);
      return null;
    }
    net = client;
    client.on('error', (m) => error(m.message));
    client.on('room', (m) => {
      // A room this browser created: its code is asked for again next time.
      if (creating && m.you === m.host && m.you >= 0) { keepCode(m.code); creating = false; }
      room = m;
      track(m.you >= 0 ? 'room' : 'watch');
      if (m.token) save({ code: m.code, token: m.token, name: m.players[m.you]?.name ?? '' });
      else if (m.you < 0) save({ code: m.code, token: '', name: $<HTMLInputElement>('o-name').value.trim(), watch: true });
      ready = m.players[m.you]?.ready ?? false;
      if (m.state === 'lobby') renderRoom();
      hooks.onRoom(m);
    });
    client.on('start', (m) => { if (room) hooks.onStart(m, client, room); });
    client.on('rooms', (m) => { renderList(m.rooms); });
    client.on('found', renderInstance);
    client.on('discordUser', (m) => { discordIdentity(m.name); });
    // Discord: the player's name, read by the server with the code of its authorization (one code per connection).
    if (discordInstance) void discordCode().then((code) => { if (code) client.send({ t: 'discord', code }); else discordIdentity(null); });
    client.on('kicked', (m) => {
      // Out of the room: no way back with this seat (its token is no longer good), back to the form. The connection
      // stays open: the server refuses it a seat in that room again (a reload may come back, as a new browser would).
      save(null);
      room = null;
      ready = false;
      hooks.onKicked(m.message);
      showForm();
      error(m.message);
    });
    client.onClose(() => {
      if (room?.state === 'lobby') { error('Connection lost.'); showForm(); }
    });
    return client;
  }

  $('o-create').onclick = async () => {
    error('');
    const n = name();
    if (!n) return;
    const c = await connect();
    const a = arsenal();
    const chosen = isDefaultArsenal(a) ? '' : encodeArsenal(a.ammo, a.chestWeapons);
    creating = true;
    c?.send({
      t: 'create', name: n, players: size.players(), units: size.units(), lava: size.lava(), map: map.value, arsenal: chosen,
      turn: Number($<HTMLSelectElement>('o-turn').value), friendlyFire: $<HTMLInputElement>('o-ff').checked, hidden: $<HTMLInputElement>('o-hidden').checked,
      free: $<HTMLInputElement>('o-free').checked, ghosts: $<HTMLInputElement>('o-ghosts').checked, public: listed,
      pings: PING_RULES[Number(pings.value)],
      code: lastCode(),
      ...(discordInstance ? { instance: discordInstance } : {}),
    });
  };

  // A spectator needs no name: only the code.
  async function watchRoom(code: string): Promise<void> {
    error('');
    if (code.length !== 4) { error('The code has 4 characters.'); return; }
    const c = await connect();
    c?.send({ t: 'watch', code });
  }

  async function joinRoom(code: string): Promise<void> {
    error('');
    const n = name();
    if (!n) return;
    if (code.length !== 4) { error('The code has 4 characters.'); return; }
    const c = await connect();
    c?.send({ t: 'join', code, name: n });
  }

  const typedCode = () => $<HTMLInputElement>('o-code').value.trim().toUpperCase();
  $('o-watch').onclick = () => { void watchRoom(typedCode()); };
  $('o-join').onclick = () => { void joinRoom(typedCode()); };

  // --- public rooms -------------------------------------------------------------------------------
  /** Asked for while the « Join » tab is on screen (and the page is), every LIST_EVERY_MS. */
  async function refreshList(): Promise<void> {
    if (room || document.hidden || $('sub-join').offsetParent === null) return;
    const c = await connect(true);
    c?.send({ t: 'list' });
  }
  window.setInterval(() => { void refreshList(); }, LIST_EVERY_MS);
  document.addEventListener('visibilitychange', () => { void refreshList(); });

  // --- Discord: the room of the Activity -----------------------------------------------------------
  /**
   * Author's mode: nobody creates the room of an Activity. « Join » opens it for the first of its players (the host, who
   * starts the match) and seats the others in it; its settings are the server's. Looked for while the lobby is on screen,
   * to say who opened it and how many seats are taken.
   */
  async function refreshInstance(): Promise<void> {
    if (!discordInstance || room || document.hidden || $('d-lobby').offsetParent === null) return;
    const c = await connect(true);
    c?.send({ t: 'find', instance: discordInstance });
  }
  let instanceCode = '';
  let instanceOpen = true;
  function renderInstance(m: FoundMessage): void {
    const r = m.room;
    instanceCode = r?.code ?? '';
    instanceOpen = !r || (r.state === 'lobby' && r.players < r.max);
    $('d-status').innerHTML = !r ? 'Nobody has opened the room of this Activity yet: join it, you will start the match.'
      : r.state === 'playing' ? `<b>${escapeHtml(r.host)}</b>'s match is in progress: you can watch it.`
        : `<b>${escapeHtml(r.host)}</b> opened the room · ${r.players} / ${r.max} players${r.players >= r.max ? ' (full)' : ''}`;
    renderDiscordButtons();
  }
  /**
   * The player's Discord name (null: none, the server could not read it, or the player refused: a name is typed). Once
   * known, it is the team name and cannot be changed; « Join » waits for the answer.
   */
  let identity: 'asking' | 'known' | 'none' = 'asking';
  function discordIdentity(name: string | null): void {
    const field = $<HTMLInputElement>('o-name');
    if (name) {
      identity = 'known';
      field.value = name;
      field.readOnly = true;
      field.title = 'Your Discord name';
    } else if (identity === 'asking') identity = 'none';
    renderDiscordButtons();
  }
  function renderDiscordButtons(): void {
    $('d-join').classList.toggle('hidden', !instanceOpen);
    $<HTMLButtonElement>('d-join').disabled = identity === 'asking';
    $<HTMLButtonElement>('d-watch').disabled = instanceCode === '';
  }
  $('d-join').onclick = async () => {
    if (!discordInstance) return;
    error('');
    const n = name();
    if (!n) return;
    const c = await connect();
    c?.send({ t: 'create', name: n, instance: discordInstance });
  };
  $('d-watch').onclick = () => { void watchRoom(instanceCode); };
  if (discordInstance) {
    renderDiscordButtons();
    window.setInterval(() => { void refreshInstance(); }, FIND_EVERY_MS);
    void refreshInstance();
  }

  function renderList(rooms: ListedRoom[]): void {
    const openCount = rooms.filter((r) => r.state === 'lobby' && r.players < r.settings.players).length;
    $('o-rooms-note').textContent = rooms.length === 0 ? '' : `${openCount} open · ${rooms.length - openCount} to watch`;
    if (rooms.length === 0) {
      $('o-rooms').innerHTML = '<li class="empty">No public room for now: create one!</li>';
      return;
    }
    $('o-rooms').innerHTML = rooms.map((r) => {
      const s = r.settings;
      const joinable = r.state === 'lobby' && r.players < s.players;
      const state = r.state === 'playing' ? '<span class="live">● in progress</span>' : joinable ? '' : '<span class="live">full</span>';
      const watchers = r.watchers > 0 ? ` · 👁 ${r.watchers}` : '';
      return `<li><div class="pr-head"><b>${escapeHtml(r.code)}</b><span class="host" title="Host">${escapeHtml(r.host)}</span>${state}`
        + `<span class="seats">${r.players} / ${s.players} players${watchers}</span></div>`
        + `<div class="pr-rules">${escapeHtml(rulesSummary(s))}</div>`
        + '<div class="pr-buttons">'
        + (joinable ? `<button class="join" data-code="${escapeHtml(r.code)}">Join</button>` : '')
        + `<button class="watch" data-code="${escapeHtml(r.code)}" title="Enter the room without a seat">👁 Watch</button></div></li>`;
    }).join('');
    $('o-rooms').querySelectorAll<HTMLButtonElement>('button.join').forEach((b) => { b.onclick = () => { void joinRoom(b.dataset.code ?? ''); }; });
    $('o-rooms').querySelectorAll<HTMLButtonElement>('button.watch').forEach((b) => { b.onclick = () => { void watchRoom(b.dataset.code ?? ''); }; });
  }

  // --- room ---------------------------------------------------------------------------------------
  function showForm(): void {
    $('lobby-form').classList.remove('hidden');
    $('lobby-room').classList.add('hidden');
  }

  function renderRoom(): void {
    if (!room) return;
    $('lobby-form').classList.add('hidden');
    $('lobby-room').classList.remove('hidden');
    const s = room.settings;
    const watching = room.you < 0;
    const watchers = room.watchers ?? 0;
    $('r-code').textContent = `Room ${room.code}`;
    $('r-count').textContent = `${room.players.length} / ${s.players} players` + (watchers > 0 ? ` · ${watchers} spectator${watchers > 1 ? 's' : ''}` : '');
    $('r-watching').classList.toggle('hidden', !watching);
    $('r-ready').classList.toggle('hidden', watching);
    $('r-link').textContent = roomLink(room.code);
    const rows: string[] = [];
    const hosting = room.you === room.host && room.you >= 0;
    const armed = kickArmed.until > performance.now() ? kickArmed.seat : -1;
    for (let seat = 0; seat < s.players; seat++) {
      const p = room.players[seat];
      if (!p) { rows.push('<li class="empty">Waiting for a player…</li>'); continue; }
      const tags = [seat === room.host ? 'host' : '', p.ready ? '<b>ready</b>' : ''].filter(Boolean).join(' · ');
      // The host may exclude the others (a second click confirms).
      const kick = hosting && seat !== room.host
        ? ` <button class="linkbtn kick${armed === seat ? ' armed' : ''}" data-seat="${seat}" title="Kick ${escapeHtml(p.name)} from the room">${armed === seat ? 'confirm?' : 'kick'}</button>`
        : '';
      rows.push(`<li><span style="color:${TEAM_COLORS[seat]};font-weight:600">${escapeHtml(p.name)}</span><span class="tags">${tags}${kick}</span></li>`);
    }
    $('r-players').innerHTML = rows.join('');
    $('r-players').querySelectorAll<HTMLButtonElement>('button.kick').forEach((b) => {
      b.onclick = () => {
        const seat = Number(b.dataset.seat);
        if (kickArmed.seat === seat && kickArmed.until > performance.now()) {
          kickArmed = { seat: -1, until: 0 };
          net?.send({ t: 'kick', seat });
        } else {
          kickArmed = { seat, until: performance.now() + KICK_CONFIRM_MS };
          window.setTimeout(() => { if (room?.state === 'lobby') renderRoom(); }, KICK_CONFIRM_MS + 50);
        }
        renderRoom();
      };
    });
    $('r-summary').textContent = `${s.discord ? 'Room of the Discord Activity' : s.public ? 'Public room' : 'Private room'} · ${rulesSummary(s, room.players.length)}`;
    $('r-ready').textContent = ready ? 'Ready ✓' : 'I\'m ready';
    $('r-ready').classList.toggle('on', ready);
    const host = room.you === room.host;
    // The match may start with the players who are there (2 at least, all ready): the empty seats stay empty.
    const allReady = room.players.length >= 2 && room.players.every((p) => p.ready);
    $('r-start').classList.toggle('hidden', !host);
    $<HTMLButtonElement>('r-start').disabled = !allReady;
    $('r-start').textContent = allReady && room.players.length < s.players ? `Start with ${room.players.length} players` : 'Start the match';
  }

  $('r-ready').onclick = () => { net?.send({ t: 'ready', ready: !ready }); };
  // Over http on the local network the Clipboard API does not exist: copyText falls back, else the link is selected.
  $('r-copy').onclick = async () => {
    if (!room) return;
    const ok = await copyText(roomLink(room.code));
    if (!ok) selectText($('r-link'));
    $('r-copy').textContent = ok ? 'link copied ✓' : 'selected: Ctrl+C to copy it';
    window.setTimeout(() => { $('r-copy').textContent = 'copy the link'; }, 2500);
  };
  $('r-start').onclick = () => {
    if (!room || !net) return;
    const s = room.settings;
    // The host draws the map (if it is random) and the seed; the server only relays the scenario.
    const scenario: ScenarioDesc = {
      terrain: resolveMap(s.map), seed: s.map === map.value ? previewSeed : (Math.random() * 2 ** 32) >>> 0, width: MAP_W, height: MAP_H,
      mode: 'match', match: { players: room.players.length, unitsPerPlayer: unitsAtStart(s, room.players.length), roundsBeforeLava: s.lava, ...decodeArsenal(s.arsenal ?? ''), ...matchOptions(s) },
    };
    net.send({ t: 'start', scenario });
  };

  function leave(): void {
    net?.send({ t: 'leave' });
    net?.close();
    net = null;
    room = null;
    ready = false;
    save(null);
    showForm();
    void refreshList();
  }
  $('r-leave').onclick = leave;

  // --- link and return -----------------------------------------------------------------------------
  const params = new URLSearchParams(location.search);
  const linked = params.get('room')?.toUpperCase() ?? null;
  const saved = loadSaved();
  if (saved) $<HTMLInputElement>('o-name').value = saved.name;
  else if (loadOptions().teamName) $<HTMLInputElement>('o-name').value = loadOptions().teamName;
  if (linked && !discordInstance) {
    $<HTMLInputElement>('o-code').value = linked;
    showSub('join');
  }
  // Back to the same seat after a reload of the tab (the token proves it is ours).
  if (saved && (!linked || linked === saved.code)) {
    void connect().then((c) => c?.send(saved.watch ? { t: 'watch', code: saved.code } : { t: 'join', code: saved.code, token: saved.token, name: saved.name }));
  }

  /** Online, during the match: the host excludes a seat (the server sends its `forfeit` to every browser). */
  function kick(seat: number): void {
    net?.send({ t: 'kick', seat });
  }

  return { leave, kick };
}
